import type { FC } from 'react';
import { generatePath } from 'react-router-dom';
import InputCheckbox from '@components/InputCheckbox';
import { InputToggleGroup } from '@components/InputToggleGroup';
import type { DbEndorsement } from '@domain/schemas/contract.schema';
import { ResourceState } from '@domain/schemas/contract.schema';
import { ExternalLinkIcon, InformationCircleIcon } from '@heroicons/react/outline';
import { ExclamationIcon } from '@heroicons/react/solid';
import { StatusBadge } from '@pages/User/components';
import type { MergeOrder } from '@root/helpers';
import { formatDate, getEndorsementDisplayName, getFinalisedAtDate, sortEndorsementsForMerge } from '@root/helpers';
import Nav from '@src/routes';
import clsx from 'clsx';

import type { MergeEndorsementsSelection } from '../types';

const ORDER_OPTIONS = [
  { label: 'Effective from date', value: 'effective' },
  { label: 'Date finalised', value: 'finalised' },
];

const COPY = {
  noEndorsements: 'This contract has no endorsements to merge',
  someNotFinalised: 'Some of the endorsements on this contract are not finalised, so they cannot be merged',
  noneFinalised: 'None of the endorsements on this contract are finalised, so they cannot be merged',
  loadFailed: "We couldn't load this contract's endorsements, so none will be merged into this renewal",
  caveat:
    'Endorsement changes will be merged into the renewal as accurately as possible, but some changes may not carry across. Double-check the renewal content before proceeding.',
};

// The heading row and the endorsement rows are separate flex rows, so their column widths live here
// to stay in step — a mismatch shifts the headings away from the values under them.
const COLUMN = {
  lead: 'w-4 shrink-0',
  name: 'min-w-0 flex-1',
  status: 'w-24 shrink-0',
  date: 'w-24 shrink-0',
  action: 'w-4.5 shrink-0',
} as const;

const InfoNotice: FC<{ content: string }> = ({ content }) => (
  <div className="flex items-start gap-x-3 rounded-md bg-gray-50 p-4">
    <InformationCircleIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
    <p className="text-sm leading-5 font-medium text-gray-600">{content}</p>
  </div>
);

const WarningNotice: FC<{ content: string }> = ({ content }) => (
  <div className="flex items-start gap-x-3 rounded-md bg-amber-50 p-4">
    <ExclamationIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
    <p className="text-sm leading-5 font-medium text-amber-800">{content}</p>
  </div>
);

export const MergeEndorsementsUnavailableNotice: FC = () => <WarningNotice content={COPY.loadFailed} />;

const isFinal = (endorsement: DbEndorsement) => endorsement.status === ResourceState.FINAL;

export interface MergeEndorsementsSectionProps {
  contractId: string;
  endorsements: DbEndorsement[];
  onChange: (value: MergeEndorsementsSelection) => void;
  value: MergeEndorsementsSelection;
}

export const MergeEndorsementsSection: FC<MergeEndorsementsSectionProps> = ({
  contractId,
  endorsements,
  onChange,
  value,
}) => {
  const { endorsementIds: selectedIds, order } = value;

  const sorted = sortEndorsementsForMerge(endorsements, order);

  const hasFinal = endorsements.some(isFinal);
  const hasNonFinal = endorsements.some((endorsement) => !isFinal(endorsement));

  const toggleSelected = (id: string) =>
    onChange({
      ...value,
      endorsementIds: selectedIds.includes(id) ? selectedIds.filter((v) => v !== id) : [...selectedIds, id],
    });

  if (endorsements.length === 0) {
    return (
      <section className="w-full" aria-label="Merge Endorsements">
        <InfoNotice content={COPY.noEndorsements} />
      </section>
    );
  }

  return (
    <section className="flex w-full min-w-0 flex-col gap-6" aria-label="Merge Endorsements">
      <h3 className="pt-6 text-center text-lg leading-6 font-medium text-gray-900">Merge Endorsements</h3>

      {hasNonFinal && <WarningNotice content={hasFinal ? COPY.someNotFinalised : COPY.noneFinalised} />}

      <InputToggleGroup
        isFullWidth
        item={{}}
        labelText="Select the order to merge endorsements"
        name="merge-endorsements-order"
        onChange={(next) => next && onChange({ ...value, order: next as MergeOrder })}
        options={ORDER_OPTIONS}
        value={order}
      />

      <div className="flex flex-col gap-1">
        <p className="text-sm leading-5 font-medium text-gray-700">Select the endorsements to merge</p>
        <div className="max-h-72 min-w-0 overflow-y-auto rounded-lg border border-gray-200 shadow-sm">
          <div className="sticky top-0 z-10 flex items-center gap-3 rounded-t-lg bg-gray-50 px-4 py-2.5 text-xs font-medium tracking-wider text-gray-500 uppercase">
            <span className={COLUMN.lead} />
            <span className={COLUMN.name}>Name</span>
            <span className={clsx(COLUMN.status, 'text-center')}>Status</span>
            <span className={COLUMN.date}>{order === 'effective' ? 'Effective' : 'Finalised'}</span>
            <span className={COLUMN.action} />
          </div>
          <ul className="divide-y divide-gray-200 rounded-b-lg">
            {sorted.map((endorsement) => {
              const isSelectable = isFinal(endorsement);
              const date = order === 'effective' ? endorsement.effective_date : getFinalisedAtDate(endorsement);
              const displayName = getEndorsementDisplayName(endorsement);

              return (
                <li
                  key={endorsement.id}
                  aria-disabled={!isSelectable}
                  className={clsx('flex min-w-0 items-center gap-3 px-4 py-4', !isSelectable && 'text-gray-400')}
                >
                  <span className={COLUMN.lead}>
                    {isSelectable && (
                      <InputCheckbox
                        aria-label={`Merge ${displayName}`}
                        id={endorsement.id}
                        isChecked={selectedIds.includes(endorsement.id)}
                        onChange={() => toggleSelected(endorsement.id)}
                      />
                    )}
                  </span>
                  <div className={COLUMN.name}>
                    <p className="truncate text-sm font-normal" title={displayName}>
                      {displayName}
                    </p>
                    {endorsement.internal_description && (
                      <p
                        className={clsx('truncate text-xs text-gray-400', isSelectable && 'text-gray-600')}
                        title={endorsement.internal_description}
                      >
                        {endorsement.internal_description}
                      </p>
                    )}
                  </div>
                  <span className={clsx(COLUMN.status, 'flex justify-center')}>
                    <StatusBadge
                      className={clsx('whitespace-nowrap', !isSelectable && 'opacity-60')}
                      status={endorsement.status}
                    />
                  </span>
                  <span className={clsx(COLUMN.date, 'text-sm whitespace-nowrap', isSelectable && 'text-gray-500')}>
                    {date ? formatDate(date, 'DD MMM YYYY') : ''}
                  </span>
                  <a
                    aria-label={`Open ${displayName} in a new tab`}
                    className={COLUMN.action}
                    href={generatePath(Nav.EndorsementView, { id: contractId, endorsement_id: endorsement.id })}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <ExternalLinkIcon className="text-primary-600 size-4.5" />
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {hasFinal && <InfoNotice content={COPY.caveat} />}
    </section>
  );
};
